/**
 * Live product shape <-> screen shape.
 *
 * The Products screen was written against the mock data in ../mock/dataset.js.
 * The real endpoint answers with a different shape, so rather than teach the
 * screen two shapes, everything is translated here on the way in and on the
 * way out. Pure functions only -- no axios, no mock imports -- so api/index.js
 * stays the one file that knows mocking exists.
 *
 * The differences that matter:
 *   - the list is wrapped as { detail, result: { data, total_count, ... } }
 *   - product_type comes back as an object, the screen wants its name
 *   - "no logo" comes back as "" rather than null
 *   - processing_fees and nested eligibility do not exist server-side
 */

/** One row from AdminProductListSerializer. */
export function productRow(raw) {
  const type = raw.product_type || null;
  return {
    id: raw.id,
    name: raw.name,
    title: raw.title || raw.name,
    // The serializer returns "" for "no logo". Both are falsy so the template
    // would cope either way, but null matches the mock and reads as deliberate.
    logo: raw.logo || null,
    product_type: type ? type.loan_type : null,
    // Kept for the edit form, which has to send an id rather than a label.
    product_type_id: type ? type.id : null,
    is_active: !!raw.is_active,
    interest_rate: raw.interest_rate || "-",
    tenure: raw.tenure || "-",
    // A CharField server-side: legacy rows hold prose like "Loan upto Rs.20
    // Lakhs", so it is deliberately not coerced to a number here.
    loan_upto: raw.loan_upto,
    priority: raw.priority,
    // 1-based position in the global ranking of EVERY product, hidden ones
    // included -- the unit the move API speaks, not what customers see.
    rank: raw.rank == null ? null : raw.rank,
    // Where customers actually see it -- see customerPosition() below.
    customer_rank: raw.customer_rank == null ? null : raw.customer_rank,
    customer_rank_by_type: raw.customer_rank_by_type || {},
    hidden_reason: raw.hidden_reason || null,
    flag_recomended: !!raw.flag_recomended,
    enable_manual_application: raw.enable_manual_application !== false,
    web_url: raw.web_url || "",
    utm_link: raw.utm_link || "",
    created: raw.created,
    // The list endpoint carries none; openEdit falls back to its defaults.
    eligibility: null,
  };
}

/** "Personal Loan" -> "PL", "Loan Against Security" -> "LAS". */
function loanTypeShort(loanType) {
  return String(loanType)
    .split(/\s+/)
    .map((word) => word[0])
    .join("")
    .toUpperCase();
}

/**
 * Where customers see a product, for the "Customers see" cell.
 *
 * Rank counts every product, but the app and website drop disabled and
 * manual-apply-off products and always ask for one loan type, so Rank N is not
 * the Nth card a customer sees. The server works out the real places
 * (AdminProductListSerializer customer_rank / customer_rank_by_type); this only
 * words them. Reads raw rows and productRow() rows alike -- the names match.
 *
 * `loanType` is the tab on screen: on a loan-type tab the headline is the place
 * in that type's list, on "All" (or nothing) the place in the unfiltered list.
 *
 * Returns { hidden, place, types, title }:
 *   hidden -- why customers cannot see it ("Disabled", ...) or null
 *   place  -- the headline number, or null
 *   types  -- "PL 2 · CC 1", one entry per loan type the product carries
 *   title  -- the same in full words, for the hover text
 */
export function customerPosition(row, loanType) {
  if (row.hidden_reason) {
    return { hidden: row.hidden_reason, place: null, types: "", title: "" };
  }
  const byType = row.customer_rank_by_type || {};
  const entries = Object.keys(byType).map((type) => [type, byType[type]]);
  const place = loanType && loanType in byType ? byType[loanType] : row.customer_rank;
  return {
    hidden: null,
    place: place == null ? null : place,
    types: entries.map(([type, n]) => `${loanTypeShort(type)} ${n}`).join(" · "),
    title: [
      row.customer_rank == null ? null : `All products: #${row.customer_rank}`,
      ...entries.map(([type, n]) => `${type}: #${n}`),
    ].filter(Boolean).join("\n"),
  };
}

/** The paged list envelope, flattened to what DataTable already expects. */
export function productList(envelope) {
  const result = (envelope && envelope.result) || {};
  return {
    results: (result.data || []).map(productRow),
    total_count: result.total_count || 0,
    total_page_count: result.total_page_count || 1,
    page_length: result.page_length,
  };
}

/**
 * "492001, 110001 ,,110002" -> ["492001", "110001", "110002"].
 *
 * Tolerates an array already, so a caller that holds the parsed list rather
 * than the typed string does not have to join it first.
 */
function splitList(value) {
  if (Array.isArray(value)) return value;
  return String(value || "")
    .split(",")
    .map((v) => v.trim())
    .filter(Boolean);
}

/**
 * `rank` for the create/update APIs, or nothing.
 *
 * The server places the product at that position (1 = shown first) and
 * renumbers priorities itself. Sent only when the admin asked for a position:
 * blank on create leaves the product at the bottom, and an unchanged box on
 * edit must not re-apply a rank that another admin may have moved since.
 */
function rankField(form) {
  const rank = parseInt(form.rank, 10);
  if (!rank || rank < 1) return {};
  if (form.original_rank != null && rank === Number(form.original_rank)) return {};
  return { rank };
}

/**
 * ProductCreateSerializer requires a nested eligibility_details list and
 * product_type_ids, neither of which survives multipart encoding -- which is
 * why the logo is always a separate follow-up request.
 */
export function productCreatePayload(form, typeId) {
  return {
    name: form.name,
    // Offers are matched back to products by title, so it has to be set.
    title: form.name,
    product_type_ids: [typeId],
    is_active: !!form.is_active,
    flag_recomended: !!form.flag_recomended,
    interest_rate: form.interest_rate,
    tenure: form.tenure,
    loan_upto: String(form.loan_upto || ""),
    web_url: form.web_url,
    utm_link: form.utm_link,
    ...rankField(form),
    enable_manual_application: true,
    eligibility_details: [
      {
        product_type: typeId,
        minimum_age: Number(form.minimum_age) || 21,
        maximum_age: Number(form.maximum_age) || 58,
        minimum_income: form.minimum_income || null,
        maximum_income: form.maximum_income || null,
        cibil_score: form.cibil_score || null,
        // Lists end to end: MultiChoiceListField serialises and accepts arrays.
        employment_type: form.employment_type || [],
        salary_type: form.salary_type || [],
        pincode_list: splitList(form.pincode_list),
        excluded_pincode_list: splitList(form.excluded_pincode_list),
      },
    ],
  };
}

/**
 * The update serializer has no eligibility fields, so they are left out.
 *
 * `logoRemove` sends an explicit null: Product.logo is null=True, so DRF
 * accepts null to clear it. Multipart cannot express that -- an empty string
 * would be rejected as "not a file" -- so clearing is a JSON PATCH.
 */
export function productUpdatePayload(form, typeId, logoRemove) {
  return {
    name: form.name,
    title: form.name,
    product_type_ids: [typeId],
    is_active: !!form.is_active,
    flag_recomended: !!form.flag_recomended,
    interest_rate: form.interest_rate,
    tenure: form.tenure,
    loan_upto: String(form.loan_upto || ""),
    web_url: form.web_url,
    utm_link: form.utm_link,
    ...rankField(form),
    ...(logoRemove ? { logo: null } : {}),
  };
}

/** One row from ProductEligibilityDetailSerializer, for the detail screen. */
export function eligibilityRow(raw) {
  if (!raw) return null;
  return {
    // The row's own id -- eligibility_update/ is keyed on it, not on the
    // product, so the editor cannot save without it.
    id: raw.id,
    product_name: raw.product_name,
    product_type_name: raw.product_type_name,
    product_type_id: raw.product_type,
    minimum_age: raw.minimum_age,
    maximum_age: raw.maximum_age,
    minimum_income: raw.minimum_income,
    maximum_income: raw.maximum_income,
    employment_type: raw.employment_type,
    salary_type: raw.salary_type,
    cibil_score: raw.cibil_score,
    // Both lists kept whole, not just counted: the editor round-trips them,
    // and a PATCH that sent back a count would wipe the list.
    pincode_list: raw.pincode_list || [],
    excluded_pincode_list: raw.excluded_pincode_list || [],
    pincode_count: (raw.pincode_list || []).length,
  };
}

/* ------------------------------------------------------- users / customers */

/**
 * One row from the live customer_list endpoint.
 *
 * The screen was written against the mock's field names; the live endpoint
 * uses its own. Mapping here rather than in the page keeps the table markup
 * untouched and means the list and the Excel export describe the same row.
 */
export function customerRow(raw) {
  return {
    id: raw.id,
    user_id: raw.id,
    customer_id: raw.customer_id,
    name: raw.name || "-",
    mobile: raw.mobile,
    email: raw.email || "",
    city: raw.city || "-",
    state: raw.state || "",
    age: raw.age || null,
    gender: raw.gender || "",
    monthly_income: raw.salary && raw.salary !== "-" ? raw.salary : null,
    employer_name: dashless(raw.employer_name),
    employment_type: dashless(raw.employment_type),
    cibil_score: raw.cibil_score || null,
    // The template reads these two names, not a single "offers".
    offer_count: raw.offer_count || 0,
    clicked_count: raw.clicked_count || 0,
    // Sum of what this customer was offered, scoped to the selected section by
    // the server. Null when none of their offers carried a parseable amount, so
    // the cell can show a dash rather than a misleading zero.
    offered_amount:
      typeof raw.offered_amount === "number" ? raw.offered_amount : null,
    // What they were actually PAID, across their disbursed offers. A different
    // figure from offered_amount and not a small difference -- the ceiling runs
    // several times the money that moved -- and unlike that one it does not
    // change with the selected section.
    disbursed_amount:
      typeof raw.disbursed_amount === "number" ? raw.disbursed_amount : null,
    stage: raw.lead_status || raw.current_loan_type || "-",
    // Where the lead stopped in onboarding -- a different question from `stage`,
    // which is the loan outcome. Server-side this is UserProfileData.profile_status.
    profile_stage: dashless(raw.task),
    kyc: kycStatuses(raw.kyc),
    platform: dashless(raw.platform),
    signed_up_date: isoDate(raw.created),
    last_active_date: isoDate(raw.last_activity),
    follow_up_date: raw.follow_up_date,
    agent_assigned: raw.agent_assigned,
    lead_source: leadSource(raw),
    utm: utmDetails(raw),
  };
}

/** The endpoint writes "-" for "nothing here"; the table renders its own dash. */
function dashless(value) {
  return !value || value === "-" ? "" : value;
}

/**
 * The three KYC checks, flattened for the table.
 *
 * The server sends a whole UserKycInfo object; the column only needs whether
 * each of the three passed. `null` means no KYC record exists at all, which is
 * a different state from "record exists, all three still pending" -- the first
 * reads as "Not started", the second as three empty rings.
 */
function kycStatuses(kyc) {
  if (!kyc || typeof kyc !== "object") return null;
  return {
    pan: kyc.pan_status || "",
    aadhaar: kyc.aadhar_status || "",
    selfie: kyc.selfie_status || "",
  };
}

/**
 * Where the lead came from, most specific first.
 *
 * `referred_by` is an object ({name, mobile}) server-side. This used to be
 * assigned straight to a string field, so any referred lead rendered as
 * "[object Object]" -- it was never displayed, which is why nobody saw it.
 *
 * The named lead partner beats a customer referral, which beats the UTM tags,
 * because that is the order of how deliberately the lead was acquired.
 */
/**
 * The six attribution values, kept as a group for the UTM column's popup.
 *
 * Both list endpoints have always sent these -- customer_list/ and
 * precheck_details_fast/ each name all six in their serializer -- and both row
 * mappers below dropped every one of them. Only utm_source and utm_medium
 * survived at all, collapsed by leadSource() into the one string the Source
 * column shows, and only when there was no lead partner and no referral.
 *
 * Grouped under `utm` rather than spread onto the row so the cell can hand the
 * whole thing to ViewUTMDetail, which reads exactly these key names.
 */
function utmDetails(raw) {
  return {
    utm_source: raw.utm_source || "",
    utm_medium: raw.utm_medium || "",
    utm_campaign: raw.utm_campaign || "",
    utm_content: raw.utm_content || "",
    utm_term: raw.utm_term || "",
    utm_platform: raw.utm_platform || "",
  };
}

function leadSource(raw) {
  if (raw.lead_partner) return raw.lead_partner;
  if (raw.referred_by && raw.referred_by.name) {
    return `Referral · ${raw.referred_by.name}`;
  }
  if (raw.utm_source) {
    return raw.utm_medium ? `${raw.utm_source} / ${raw.utm_medium}` : raw.utm_source;
  }
  return "";
}

/**
 * The customer_list envelope, flattened to what DataTable expects.
 *
 * `total_count` is read straight off the envelope now. It used to be passed in,
 * because this endpoint reported pages and not rows, so the screen re-called it
 * with page_length=1 and used the page count AS the row count -- an extra trip
 * through the most expensive query on the screen to learn a number the
 * paginator already had. The server returns it directly (webpanel/apis.py).
 *
 * `count_is_capped` is true when the server stopped counting at its cap, so a
 * screen can render "100,000+" rather than a figure that quietly stopped being
 * true. Nothing renders it yet; it is carried so it is there when a table gets
 * big enough to need it.
 */
export function customerList(envelope) {
  const rows = (envelope && envelope.customer_list) || [];
  return {
    results: rows.map(customerRow),
    total_page_count: (envelope && envelope.total_page_count) || 1,
    total_count: (envelope && envelope.total_count) || 0,
    count_is_capped: !!(envelope && envelope.count_is_capped),
  };
}

/* ------------------------------------------------- applications / precheck */

/**
 * A date the live web-panel endpoints hand back already formatted for display
 * ("19 Aug 2026 11:21 AM"). The screens run their own formatters over these
 * fields, and feeding a display string into them produced two overlapping
 * strings in one narrow column. Back to ISO so the screen can format it once.
 */
function isoDate(value) {
  if (!value) return null;
  const t = Date.parse(value);
  return isNaN(t) ? value : new Date(t).toISOString();
}

/**
 * The rupee figure inside an offer's `loan_upto`.
 *
 * Product-supplied and not reliably a number. Most offers hold a bare "500000",
 * but every Karrofin Business Loan holds the sentence "Loan upto Rs.20 Lakhs" --
 * 83 of the offers in the database. `Number()` returned NaN on those and they
 * fell through to null, which was invisible while the column only ever showed a
 * single offer's figure and becomes a silent shortfall now that it sums them.
 *
 * A value this cannot read stays null rather than becoming 0: a gap in a column
 * of money is a question, a wrong zero is an answer.
 */
function parseAmount(value) {
  if (value === null || value === undefined) return null;
  if (typeof value === "number") return isFinite(value) ? value : null;

  const text = String(value).toLowerCase();
  // The first number in the string, separators and decimals allowed:
  // "Loan upto Rs.20 Lakhs" -> 20, "5,00,000" -> 500000.
  const match = text.replace(/,/g, "").match(/\d+(?:\.\d+)?/);
  if (!match) return null;

  const amount = Number(match[0]);
  if (!isFinite(amount)) return null;
  // Scale only when the unit is spelled out AFTER the number, so a product
  // named "Lakhpati Loan" with loan_upto "500000" is not multiplied.
  const tail = text.slice(text.indexOf(match[0]) + match[0].length);
  if (/\bcrore/.test(tail)) return amount * 10000000;
  if (/\blakh/.test(tail)) return amount * 100000;
  return amount;
}

/**
 * The offers hanging off one loan, for the expandable sub-rows.
 *
 * The payload nests them under product_offer.offer_list; the table loops over
 * `offers`, so the two were never connected and every sub-table rendered empty.
 */
function offerRows(raw) {
  const list = ((raw.product_offer || {}).offer_list) || {};
  const all = [...(list.pl_offer_list || []), ...(list.cc_offer_list || [])];
  return all.map((o) => ({
    id: o.id,
    title: o.title || o.product_type || "Offer",
    // Offers carry their own loan_status; there is no per-offer precheck.
    stage: o.loan_status || "-",
    sub_status: o.loan_sub_status || "",
    clicked: !!o.flag_offer_availed,
    // The first offer, when this one is the borrower applying for the same
    // product again within the hour (leads.repeats on the server).
    repeat_of: o.repeat_of || null,
    // Which offer the money actually went out against. The Disbursed bucket
    // narrows the row to these and totals their disbursal_amount.
    disbursed: !!o.flag_disbursed,
    disbursal_amount: o.disbursal_amount || null,
    // `loan_upto` is a display string, and not always a numeric one -- see
    // parseAmount above.
    loan_amount: parseAmount(o.loan_upto),
    reject_reason: o.remark || "",
    logo: o.bank_logo || null,
    // The affiliate link the Copy UTM link button hands over. The serializer
    // already derives it from the product's current utm_link (LeadID and
    // mobile_no substituted) and calls it redirect_url; this mapper simply
    // dropped it, so the offer rows had no link to copy.
    utm_link: o.redirect_url || "",
  }));
}

/**
 * One of a customer's offers, for the User Management popup.
 *
 * Deliberately shares field names with offerRows() above where they overlap
 * (title / stage / clicked / disbursal_amount / reject_reason), so the two
 * screens describe an offer the same way. This one keeps the detail that
 * mapper drops -- the terms, the lender and the dates -- because the popup is
 * the place someone goes to actually read an offer.
 */
export function customerOfferRow(raw) {
  const nbfc = raw.alloted_nbfc;
  return {
    id: raw.id,
    title: raw.title || raw.product_type || "Offer",
    sub_title: raw.sub_title || "",
    logo: raw.bank_logo || null,
    // alloted_nbfc is a SerializerMethodField -- an object on some rows, a
    // bare name on others. Read both rather than rendering "[object Object]".
    lender: (nbfc && (nbfc.nbfc_name || nbfc.name)) || (typeof nbfc === "string" ? nbfc : ""),
    product_type: raw.product_type || "",
    interest_rate: raw.interest_rate || "",
    processing_fees: raw.processing_fees || "",
    tenure: raw.tenure || "",
    // A display string ("500000"), not a number -- same as the product column.
    loan_upto: raw.loan_upto || "",
    // Credit cards carry these two instead of the loan terms above.
    joining_fees: raw.joining_fees || "",
    annual_charges: raw.annual_charges || "",
    clicked: !!raw.flag_offer_availed,
    repeat_of: raw.repeat_of || null,
    stage: raw.loan_status || "-",
    sub_status: raw.loan_sub_status || "",
    offer_status: raw.offer_status || "",
    disbursed: !!raw.flag_disbursed,
    disbursal_amount: raw.disbursal_amount || null,
    disbursal_date: raw.disbursal_date || null,
    reject_reason: raw.remark || "",
    redirect_url: raw.redirect_url || "",
    created: isoDate(raw.created),
    loan_id: raw.loan_id,
    loan_product_type: raw.loan_product_type || "",
  };
}

/** The customer_offers envelope, flattened. */
export function customerOfferList(envelope) {
  const result = (envelope && envelope.result) || {};
  return {
    offers: (result.offers || []).map(customerOfferRow),
    // How many loans they span. The table hides its Loan column at 1 -- a
    // column repeating one value is just noise.
    loan_count: result.loan_count || 0,
  };
}

/** One row from the live precheck_details_fast endpoint. */
export function precheckRow(raw) {
  const counts = raw.loan_count || {};
  const offer = raw.product_offer || {};
  const offerCount = (offer.pl_offer_count || 0) + (offer.cc_offer_count || 0);

  return {
    id: raw.id,
    loan_id: raw.loan_id,
    // The CustomUser pk. `customer_id` is a profile pk -- using it here opened
    // the wrong record when a row was clicked.
    user_id: raw.user_id,
    customer_id: raw.customer_id,
    name: raw.name || "-",
    mobile: raw.mobile,
    city: raw.city || "-",
    state: raw.state || "",
    // The loan's own status, not a count breakdown.
    stage: raw.loan_status || (counts.applied ? "Applied" : "-"),
    precheck_status: raw.precheck_status || "",
    offer_count: offerCount,
    // How many offers were actually clicked, not how many exist.
    clicked_count: raw.availed_offer_count || 0,
    loan_amount: raw.applied_amount || raw.eligibility_amount || null,
    disbursal_amount: raw.disbursed_amount || null,
    reject_reason: raw.rejection_reason || "",
    // initiated_on is the loan's own date. `created` is the customer's signup,
    // which made every row show the same date in the Applied column.
    applied_at: isoDate(raw.initiated_on),
    last_modified: isoDate(raw.last_modified || raw.initiated_on),
    follow_up_date: raw.follow_up_date,
    agent_assigned: raw.agent_assigned,
    lead_source: raw.lead_source || "",
    salary: raw.salary,
    // The customer columns on Offer Management. Every one of these was already
    // in the response and dropped here -- `pincode` and the two loan counts are
    // plain reads, and cibil_score/age/gender were added to the serializer
    // alongside them.
    pincode: raw.pincode || null,
    cibil_score: raw.cibil_score || null,
    age: raw.age || null,
    gender: raw.gender || "",
    // That customer's OTHER loans. `loan_count` is read above for the `stage`
    // fallback and was then thrown away.
    loans_completed: counts.completed || 0,
    loans_rejected: counts.rejected || 0,
    offers: offerRows(raw),
    utm: utmDetails(raw),
  };
}

/** The precheck envelope, flattened. `totalCount` as for customerList above. */
export function precheckList(envelope, totalCount, counts) {
  const result = (envelope && envelope.result) || {};
  const rows = result.data || [];
  return {
    results: rows.map(precheckRow),
    total_page_count: result.total_page_count || 1,
    // The list endpoint now returns `total_count` itself, so the screen no
    // longer re-runs it with page_length=1 just to read this number. The
    // explicit argument is still honoured for callers that have one.
    total_count:
      typeof totalCount === "number"
        ? totalCount
        : typeof result.total_count === "number"
        ? result.total_count
        : null,
    // NOT result.allcount -- on this endpoint that is a stub of seven zeroes,
    // which is what rendered the pipeline as a row of blank boxes reading "0".
    // The real counts come from their own endpoint; see getApplications.
    counts: counts || [],
  };
}

/**
 * The insurance envelope, flattened to the shape the screen reads.
 *
 * Unlike precheckList above, `allcount` IS the real thing here -- the server
 * computes it from the filtered queryset before applying the status filter
 * (products/apis.py, UserInsuranceProductList.list), so it is exactly the four
 * buckets the strip wants.
 *
 * `total_count` is the one gap: the endpoint does not send it, so the count
 * badge on the table header stays blank on the live path until it does. Rows
 * are passed through unchanged -- UserInsuranceDataSerializer already answers
 * with the field names the table declares.
 */
export function insuranceList(envelope) {
  const result = (envelope && envelope.result) || {};
  return {
    results: result.data || [],
    counts: result.allcount || [],
    total_page_count: result.total_page_count || 1,
    total_count: result.total_count,
  };
}

/** Active insurance products for the dropdown; `title` is what it displays. */
export function insuranceProductList(envelope) {
  const rows = (envelope && envelope.result) || [];
  return rows.map((raw) => ({
    id: raw.id,
    name: raw.name,
    title: raw.title || raw.name,
    utm_link: raw.utm_link || "",
    is_active: raw.is_active !== false,
  }));
}

/* ------------------------------------------------------------ notifications */

/**
 * The Sent history response.
 *
 * karrofin_notification_history/ was built to answer in the shape the mock
 * already produced, so this is a pass-through with defaults rather than a
 * translation -- the point is that a failed or partial response still gives the
 * page an object it can render, instead of `summary` being undefined and every
 * KPI throwing.
 */
export function notificationList(envelope) {
  const data = envelope || {};
  return {
    results: data.results || [],
    total_count: data.total_count || 0,
    total_page_count: data.total_page_count || 1,
    page_number: data.page_number || 1,
    summary: {
      total: 0,
      delivered: 0,
      delivery_rate: 0,
      failed: 0,
      by_channel: [],
      by_status: [],
      ...(data.summary || {}),
    },
  };
}

/** Segment size and per-channel reachability, with the same guard. */
export function notificationAudience(envelope) {
  const data = envelope || {};
  return {
    total: data.total || 0,
    reachable: data.reachable || {},
    sample: data.sample || [],
  };
}
